import { FetchResult } from '@apollo/client';
import { centsToDollars, createError, dollarsToCents } from '@common/neo.tax';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { useUpdatePayrollExpense } from '@neo.tax/rd/hooks';
import { PayrollQre, WithPayrollExpense } from '@neo.tax/rd/hooks/usePayrollQres';
import { ToastConfigState, UpdatePayrollExpenseMutation } from '@neo.tax/shared';

export type PayrollExpenseDetailsFormType = {
  id: string;
  amountDollars: number;
  payrollProvider: string;
  countryCodeAlpha2: string;
  title?: string;
  stateCodeAlpha2?: string;
};

type UsePayrollExpenseDetailsFormProps = {
  payrollQre: WithPayrollExpense<PayrollQre>;
};

const makeDefaultFormValues = (payrollQre: WithPayrollExpense<PayrollQre>): PayrollExpenseDetailsFormType => {
  if (payrollQre.amountCents == null) {
    throw createError('NotAuthorizedForQREAmounts', 'QRE amount is missing, this might indicate an unauthorized user');
  }
  return {
    id: payrollQre.id,
    title: payrollQre.title ?? '',
    amountDollars: centsToDollars(payrollQre.amountCents),
    payrollProvider: payrollQre.payrollProvider ?? '',
    countryCodeAlpha2: payrollQre.country ?? '',
    stateCodeAlpha2: payrollQre.state ?? '',
  };
};

export const usePayrollExpenseDetailsForm = ({
  payrollQre,
}: UsePayrollExpenseDetailsFormProps): UseFormReturn<PayrollExpenseDetailsFormType> => {
  const [updatePayrollExpense] = useUpdatePayrollExpense();

  const form = useForm<PayrollExpenseDetailsFormType>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(payrollQre),
  });
  const { handleSubmit, watch } = form;
  const currentPayrollQreId = watch('id');

  const onValid: SubmitHandler<PayrollExpenseDetailsFormType> = useCallback(
    (values): Promise<FetchResult<UpdatePayrollExpenseMutation>> | undefined => {
      const amountCents = dollarsToCents(values.amountDollars);
      const countryAlpha2 = values.countryCodeAlpha2;
      const state = countryAlpha2 === 'US' ? values.stateCodeAlpha2 : null;

      // Don't unnecessarily update the expense if the values haven't changed
      if (
        amountCents === payrollQre.amountCents &&
        countryAlpha2 === payrollQre.country &&
        state === payrollQre.state &&
        values.title === payrollQre.title
      ) {
        return;
      }

      return updatePayrollExpense(payrollQre.payrollExpenseId, {
        amountCents,
        countryAlpha2,
        state,
        title: values.title,
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [updatePayrollExpense, payrollQre.payrollExpenseId],
  );

  const onInvalid = (): void => {
    ToastConfigState.pushErrorToast('Error updating expense', {
      description: 'One or more values in the form are invalid. Please correct them to save.',
      duration: 'MEDIUM',
    });
  };
  const onInvalidDebounced = useDebouncedCallback(onInvalid, 500);

  useEffect(() => {
    const subscription = watch(() => handleSubmit(onValid, onInvalidDebounced)());
    return (): void => subscription.unsubscribe();
  }, [handleSubmit, onInvalidDebounced, onValid, watch]);

  useEffect(() => {
    if (currentPayrollQreId !== payrollQre.id) {
      form.reset(makeDefaultFormValues(payrollQre));
    }
    // We only want to reset the form when the payroll QRE changes
  }, [currentPayrollQreId, form, payrollQre]);

  return form;
};
